/**
 * @name useDepartList.js
 * @author 김권영
 * @since 2025. 03. 05.
 * @description
 * 부서 관리 로직을 담당하는 React 훅
 * 
 * @param {string} compCode - 관리자 : 선택된 사업장 코드 (전역), 유저 : 로그인 정보에 담긴 사업장 코드 
 * @returns {Object} - 함수와 상태를 반환
 * */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { get, post, put, del } from "@utils/axios_api";
import { toast } from 'react-toastify';

const fetchDepartmentList = async (params) => {
  const response = await get("/user/comp/departmentList", { params });
  return response.result;
};

export function useDepartList(compCode) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);

  const methods = useForm({
    defaultValues: {
      useYn: "Y",
    },
    shouldFocusError: false, // Focus 방지(포커스 구현시 React ref 구현 해야함)
  });
  
  const pageIndex = parseInt(searchParams.get("pageIndex") || "1", 10);
  const recordCountPerPage = parseInt(searchParams.get("pageSize") || "10", 10);

  const queryParam = {
    pageIndex,
    recordCountPerPage,
    compCode
  };

  const { data,  refetch } = useQuery({
    queryKey: ["selectDepartmentList", queryParam],
    queryFn: () => fetchDepartmentList(queryParam),
    enabled: !!compCode,  // 회사가 선택되지 않으면 조회하지 않음
    keepPreviousData: true,
    staleTime: 600000,
  });


  const onSubmit = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageIndex", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (targetPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageIndex", targetPage);
    setSearchParams(newParams);
  };

  const changePageSize = (size) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageSize", String(size));
    newParams.set("pageIndex", "1");
    setSearchParams(newParams);
  };

  const handleSelectAll = (e) => {
    if (!data || !data.resultList) return;
    const { checked } = e.target;
    if (checked) {
      const allIds = data.resultList.map((item) => item.deptCode);
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (deptCode) => {
    if (selectedItems.includes(deptCode)) {
      setSelectedItems(selectedItems.filter((id) => id !== deptCode));
    } else {
      setSelectedItems([...selectedItems, deptCode]);
    }
  };

  const createDepartment = async (formData) => {
    try {
      const requestData = {
        ...formData,
        compCode: compCode,
      }
      await post("/user/comp/insertDepartment", requestData);
      toast.success("등록 완료");
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const updateDepartment = async (formData) => {
    try {
      const requestData = {
        ...formData,
      }
      await put("/user/comp/updateDepartment", requestData);
      toast.success("수정 완료");
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteDepartment = async () => {
    if (selectedItems.length === 0) {
      toast.warn("삭제할 항목을 선택해주세요.");
      return;
    }
    if (!window.confirm("삭제 하시겠습니까?")) return;
    try {
      await del("/user/comp/deleteDepartment", { data: { deptCodes: selectedItems } });
      toast.success("삭제 완료");
      refetch();
      setSelectedItems([]);
    } catch (err) {
      console.error(err);
    }
  };



  // 체크 여부
  const isAllChecked = !!(data?.resultList && data.resultList.length > 0 && selectedItems.length === data.resultList.length);

  return {
    methods, data,
    selectedItems, isAllChecked,
    onSubmit, handlePageChange, changePageSize, handleSelectAll, handleSelectItem,
    pageIndex, recordCountPerPage,
    createDepartment, updateDepartment, deleteDepartment,
  };
}
